<template>
    <div v-if="!loaded" class="card-body text-center small text-muted">
        {{ $t('Loading...') }}
        <div class="mt-10">
            <spinner/>
        </div>
    </div>
    <div v-else>
        <sidebar v-if="expireInDays>0"></sidebar>
        <div class="main-content" v-if="expireInDays>0">
            <topbar></topbar>
            <div class="alert p-0 m-3 mt-4" :class="expireClass" v-if="!expirationNotifcationClosed && expireInDays<10">
                <div class="col text-center">
                    <span
                        class="text-transform-initial">{{
                            $t('Your account will expire on {date}. Please contact us.', {date: $moment(company.expireAt).format('DD MMMM')})
                        }}</span>
                    <button
                        class="btn pointer"
                        @click="expirationNotifcationClosed=true"
                    >
                        <i class="fe fe-x"></i>
                    </button>
                </div>
            </div>
            <router-view class="desk32-app-content" v-if="expireInDays>0"/>
        </div>
        <div v-else>
            <div v-if="expireInDays<10"
                 class="container-fluid d-flex flex-column justify-content-center align-items-center"
                 style="height: 100vh;">
                <div class="row">
                    <div class="col-xl-2 col-md-4 col-7 mx-auto mb-4">
                        <div class="text-center">
                            <img src="@/assets/img/logo.png" alt="..." class="img-fluid w-md-25 w-100"/>
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="text-center">
                            <h2 class="text-transform-initial" style="line-height: 32px" v-html="$t('account_expired', {date: $moment(company.expireAt).format('DD MMMM')})"></h2>
                        </div>
                    </div>

                    <div class="col-xl-3 col-md-6 col-10 mb-3 mx-auto">
                        <div class="text-center mb-3">
                            <img src="@/assets/img/illustrations/32desk/expired-account.svg" alt="..." class="img-fluid w-100"/>
                        </div>
                    </div>

                    <div class="col-12 text-center">
                        <button class="btn btn-lg btn-secondary pr-5 pl-5" @click="$router.push({name:'logout'})">
                            <span class="fe fe-log-out me-4" data-bs-toggle="tooltip" data-bs-placement="top" title=""
                                  data-bs-original-title="log-out" aria-label="log-out"></span>
                            {{ $t('Logout') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <permission-check resource="onlinepbx-incoming-call" seeAll>
            <IncomingCallComponent :companyId="company.id"></IncomingCallComponent>
        </permission-check>
    </div>
</template>

<script>
import Topbar from "@/components/Topbar.vue";
import Sidebar from "@/components/Sidebar.vue";
import Spinner from '@/components/Spinner.vue';
import IncomingCallComponent from '@/components/VoIP/IncomingCallComponent.vue';
import PermissionCheck from '@/plugins/PermissionCheck.vue';
import {mapActions} from "vuex";

export default {
    components: {
        Topbar,
        Spinner,
        Sidebar,
        IncomingCallComponent,
        PermissionCheck,
    },
    mounted() {
    },
    data() {
        return {
            loaded: false,
            company: {},
            expirationNotifcationClosed: false,
        }
    },
    beforeRouteUpdate(to, from, next) {
        if (from.name === 'appointments' || from.name === 'people.customer')
            this.$pageFullScreen(false);
        next();
    },
    computed: {
        expireInDays() {
            if (!this.company.expireAt) return 11;
            return this.$moment(this.company.expireAt).diff(this.$moment(), 'days') + 1;
        },
        expireClass() {
            if (this.expireInDays >= 10) return 'hidden';
            if (this.expireInDays > 3 && this.expireInDays < 10) return 'alert-warning';
            if (this.expireInDays <= 3) return 'alert-danger';
        }
    },
    created() {
        /* Preload some data */
        Promise.all([
            this.getMyCompany("mine").then(data => {
                this.company = data;

                const defaultSettings = {
                    default_language: 'ru',
                    currency: {value: 'sum', text: 'Сум'},
                    step: 15,
                    calendarStep: 30,
                    durationStep: 15,
                    appointmentServiceRequired: false,
                    dentalChart: 'fdi',
                    newElementaryExam: false,
                    enableOnlineRegistration: false,
                    useExtendedWorkOrder: false,
                    showTeethInInvoice: false
                };

                if (data.settings) {
                    this.$settings = Object.assign(this.$settings, defaultSettings, data.settings);

                    this.$locale = this.$settings.default_language;

                    let momentLocale = 'en';
                    if (this.$locale == 'ru1') {
                        momentLocale = 'ru';
                    } else {
                        momentLocale = this.$locale;
                    }
                    this.$moment.locale(momentLocale);
                }
            }),
            this.fetchServices({query: {$limit: 1000}, $sort: {priority: 1}}),
            this.fetchTreatments({query: {$limit: 1000}, $sort: {code: 1}}),
            this.fetchSpecialties({query: {$limit: 1000}}),
            this.fetchTeam({query: {$limit: 1000}}),
            this.fetchCategories({query: {$limit: 1000}}),
            this.fetchTags({query: {$limit: 100}}),
            this.fetchSources({query: {$limit: 100}}),
            this.fetchWorkPlaces({})
        ]).then(items => {
            this.loaded = true;
        });

    },

    methods: {
        ...mapActions("service", {fetchServices: "find"}),
        ...mapActions("treatment", {fetchTreatments: "find"}),
        ...mapActions("work-place", {fetchWorkPlaces: "find"}),
        ...mapActions("my-team", {fetchTeam: "find"}),
        ...mapActions('categories', {fetchCategories: 'find'}),
        ...mapActions('specialty', {fetchSpecialties: 'find'}),
        ...mapActions('tags', {fetchTags: 'find'}),
        ...mapActions('tasks', {fetchTasks: 'find'}),
        ...mapActions('sources', {fetchSources: 'find'}),
        ...mapActions('my-company', {getMyCompany: 'get'}),
    }
};
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 767px) {
    .desk32-app-content {
        padding-bottom: 80px;
    }
}

.text-transform-initial {
    text-transform: inherit;
}
</style>

